import { Card, Text, Button, Group, Badge, Stack, Modal, Select } from '@mantine/core';
import { useState } from 'react';
import { Subscription } from '../types';
import { ProductStatus } from '../../products/types';
import { useProducts } from '../../products/hooks/useProducts';
import { addProductToSubscription, removeProductFromSubscription } from '../services/SubscriptionsService';
import { notifications } from '@mantine/notifications';

interface SubscriptionItemsManagerProps {
    subscription: Subscription;
    onUpdate: () => void;
}

export const SubscriptionItemsManager = ({ subscription, onUpdate }: SubscriptionItemsManagerProps) => {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string>('');
    const [selectedPriceId, setSelectedPriceId] = useState<string>('');
    const { products } = useProducts();

    const selectedProduct = products.find(p => p.id === selectedProductId);

    const handleAddProduct = async () => {
        if (!selectedPriceId) return;
        
        try {
            await addProductToSubscription(subscription.id, selectedPriceId);
            notifications.show({
                title: 'Success',
                message: 'Product added to subscription',
                color: 'green'
            });
            setAddModalOpen(false);
            setSelectedProductId('');
            setSelectedPriceId('');
            onUpdate();
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to add product to subscription',
                color: 'red'
            });
        }
    };

    const handleRemoveProduct = async (itemId: string) => {
        try {
            await removeProductFromSubscription(subscription.id, itemId);
            notifications.show({
                title: 'Success',
                message: 'Product removed from subscription',
                color: 'green'
            });
            onUpdate();
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to remove product from subscription',
                color: 'red'
            });
        }
    };

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text fw={500}>Subscription Items</Text>
                <Button size="sm" onClick={() => setAddModalOpen(true)}>
                    Add Product
                </Button>
            </Group>

            <Stack gap="xs">
                {subscription.items.items.map((item) => (
                    <Card key={item.id} padding="sm" withBorder>
                        <Group justify="space-between" align="center">
                            <Group>
                                <Text fw={500}>{item.price.product.name}</Text>
                                <Badge variant="light">
                                    ${item.price.amount} / {item.price.billingPeriod}
                                </Badge>
                                <Text size="sm">Qty: {item.quantity}</Text>
                            </Group>
                            <Button 
                                size="xs" 
                                color="red" 
                                variant="light"
                                onClick={() => handleRemoveProduct(item.id)}
                            >
                                Remove
                            </Button>
                        </Group>
                    </Card>
                ))}
            </Stack>

            <Modal
                opened={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                title="Add Product to Subscription"
            >
                <Stack gap="md">
                    <Select
                        label="Select Product"
                        placeholder="Choose a product"
                        value={selectedProductId}
                        onChange={(value) => {
                            setSelectedProductId(value || '');
                            setSelectedPriceId('');
                        }}
                        data={products
                            .filter(product => product.status === ProductStatus.Active)
                            .map(product => ({
                                value: product.id,
                                label: product.name
                            }))}
                    />

                    {selectedProduct && selectedProduct.prices?.items && (
                        <Select
                            label="Select Price Plan"
                            placeholder="Choose a price plan"
                            value={selectedPriceId}
                            onChange={(value) => setSelectedPriceId(value || '')}
                            data={selectedProduct.prices.items.map(price => ({
                                value: price.id,
                                label: `$${price.amount} / ${price.billingPeriod}`
                            }))}
                        />
                    )}

                    <Group justify="flex-end">
                        <Button variant="light" onClick={() => setAddModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleAddProduct}
                            disabled={!selectedPriceId}
                        >
                            Add Product
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};


